import apiClient from '../utils/apiConfig';

/**
 * Service for interacting with the chat API
 */
class ChatService {
  /**
   * Fetch global chat messages
   * 
   * @param {Object} options - Options for fetching messages
   * @param {number} options.limit - Maximum number of messages to retrieve
   * @param {number} options.page - Page number for pagination
   * @param {string} options.state - Optional state filter ('false' for global)
   * @returns {Promise} - Promise resolving to message data
   */
  async fetchMessages(options = {}) {
    const { limit = 50, page = 1, state = 'false', messageChatType = 'global' } = options;
    try {
      const response = await apiClient.get('/api/chat/messages', {
        params: { limit, page, state, messageChatType }
      });
      
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        message: error.message || 'Failed to fetch messages',
        messages: []
      };
    }
  }

  /**
   * Send a new chat message
   * 
   * @param {Object} messageData - Message data to send
   * @param {string} messageData.userChatMessageContents - Message content
   * @param {string} messageData.messageType - Message type (chat, reply, convo-chat, convo-reply)
   * @param {string} messageData.messageReplyToId - ID of message being replied to (or 'false')
   * @param {string} messageData.messageChatType - Chat type (global, convo, state, state-convo)
   * @param {string} messageData.messageState - State context (or 'false' for global)
   * @returns {Promise} - Promise resolving to the created message
   */
  async sendMessage(messageData) {
    try {
      const response = await apiClient.post('/api/chat/send', messageData);
      return response.data;
    } catch (error) {
      // Return a fallback object instead of throwing, to prevent breaking the UI
      return {
        success: false,
        message: error.message || 'Failed to send message',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Delete a chat message
   * 
   * @param {string} messageId - ID of message to delete
   * @returns {Promise} - Promise resolving to deletion status
   */
  async deleteMessage(messageId) {
    try {
      const response = await apiClient.delete(`/api/chat/messages/${messageId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a message by ID
   * 
   * @param {string} messageId - ID of message to retrieve
   * @returns {Promise} - Promise resolving to the message data
   */
  async getMessageById(messageId) {
    try {
      const response = await apiClient.get(`/api/chat/messages/${messageId}`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch message',
        error: error.response?.data || error.message
      };
    }
  }
      
  /**
   * Create a new conversation from reply chain
   * 
   * @param {Object} conversationData - Data for the new conversation
   * @param {string} conversationData.title - Title of the conversation
   * @param {string} conversationData.description - Description of the conversation
   * @param {Array<string>} conversationData.originalMessageIds - IDs of the messages that started the conversation
   * @param {string} conversationData.conversationType - Type of conversation ('global' or 'state')
   * @param {string} conversationData.state - State context (or 'false' for global)
   * @param {string} conversationData.talkType - Talk type ('open' or 'sensitive')
   * @returns {Promise} - Promise resolving to the created conversation
   */
  async createConversation(conversationData) {
    try {
      const response = await apiClient.post('/api/chat/conversations', conversationData);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to create conversation',
        error: error.response?.data || error.message
      };
    }
  }
  
  /**
   * Fetch conversations with filters
   * 
   * @param {Object} options - Options for fetching conversations
   * @param {string|Array<string>} options.status - Status filter ('active', 'still', 'idle', 'archived', or 'all')
   * @param {string} options.conversationType - Type filter ('global', 'state', or 'all')
   * @param {string} options.state - State filter (two-letter code or 'false' for global)
   * @param {number} options.page - Page number for pagination
   * @param {number} options.limit - Maximum number of conversations per page
   * @param {string} options.userId - Filter for conversations with this participant
   * @returns {Promise} - Promise resolving to conversations data
   */
  async fetchConversations(options = {}) {
    try {
      const response = await apiClient.get('/api/chat/conversations', { params: options });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch conversations',
        conversations: [],
        pagination: { total: 0, page: 1, limit: 10, pages: 0 }
      };
    }
  }
  
  /**
   * Get a specific conversation by ID
   * 
   * @param {string} conversationId - ID of the conversation to retrieve
   * @returns {Promise} - Promise resolving to the conversation data
   */
  async getConversationById(conversationId) {
    try {
      const response = await apiClient.get(`/api/chat/conversations/${conversationId}`);
      
      // Also fetch the messages for this conversation
      const messagesResponse = await this.getConversationMessages(conversationId);
      
      // Combine the conversation data with its messages
      if (messagesResponse.success) {
        return {
          ...response.data,
          messages: messagesResponse.messages
        };
      }
      
      return response.data;
    } catch (error) {
      return { 
        success: false, 
        message: error.message || 'Failed to fetch message',
        error: error.response?.data || error.message
      };
    }
  }
  
  /**
   * Get messages for a specific conversation
   * 
   * @param {string} conversationId - ID of the conversation to get messages for
   * @param {Object} options - Optional parameters like page, limit
   * @returns {Promise} - Promise resolving to the messages data
   */
  async getConversationMessages(conversationId, options = {}) {
    try {
      const { page = 1, limit = 50 } = options;
      const response = await apiClient.get(`/api/chat/conversations/${conversationId}/messages`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch conversation messages',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Edit a chat message
   * 
   * @param {string} messageId - ID of message to edit
   * @param {string} newContent - Updated message content
   * @returns {Promise} - Promise resolving to the updated message
   */
  async editMessage(messageId, newContent) {
    try {
      const response = await apiClient.put(`/api/chat/messages/${messageId}`, {
        userChatMessageContents: newContent
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user details for user IDs in messages
   * 
   * @param {Array} messages - Array of messages
   * @returns {Promise<Object>} - Promise resolving to object with user details
   */
  async getUserDetailsForMessages(messages) {
    if (!messages || !messages.length) return {};
    
    // Extract unique user IDs from messages
    const userIds = [...new Set(messages.map(msg => 
      msg.fromBurrowerId || msg.userId
    ).filter(id => id))];
    
    if (!userIds.length) return {};
    
    try {
      const response = await apiClient.post('/api/users/batch', {
        userIds: userIds
      });
      
      // Create a map of user IDs to user details
      const userMap = {};
      
      if (response.data?.users) {
        response.data.users.forEach(user => {
          userMap[user.burrowerId] = user;
        });
      }
      
      return userMap;
    } catch (error) {
      return {};
    }
  }

  /**
   * Update a conversation's status
   * 
   * @param {string} conversationId - ID of conversation to update
   * @param {string} newStatus - New status (active, still, idle, archived)
   * @returns {Promise} - Promise resolving to update result
   */
  async updateConversationStatus(conversationId, newStatus) {
    try {
      const response = await apiClient.put(`/api/chat/conversations/${conversationId}/status`, {
        status: newStatus
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update conversation status',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // NEW: Conversation Messages (separate from room messages)
  // ============================================================

  /**
   * Send a message within a conversation
   * 
   * @param {string} conversationId - ID of the conversation
   * @param {Object} messageData - Message data
   * @param {string} messageData.userChatMessageContents - Message content
   * @param {string} messageData.messageType - Message type
   * @param {string} messageData.messageReplyToId - Reply target ID
   * @returns {Promise} - Promise resolving to the created message
   */
  async sendConversationMessage(conversationId, messageData) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/messages`, messageData);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send conversation message',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Add a participant to a conversation (direct join for open conversations)
   * 
   * @param {string} conversationId - ID of the conversation
   * @param {string} userId - ID of user to add
   * @returns {Promise} - Promise resolving to result
   */
  async addParticipant(conversationId, userId) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/participants`, { userId });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to add participant',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // NEW: Mark-as-Read & Unread Counts (watermark system)
  // ============================================================

  /**
   * Mark a room or conversation as read (updates lastReadAt watermark)
   * 
   * @param {Object} params - { roomId } or { conversationId } or { messageChatType, state }
   * @returns {Promise} - Promise resolving to result
   */
  async markAsRead(params) {
    try {
      const response = await apiClient.post('/api/chat/mark-read', params);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to mark as read',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Get unread message counts for all rooms/conversations the user is in
   * 
   * @returns {Promise} - Promise resolving to { counts: { public: N, community: N, ... } }
   */
  async getUnreadCounts() {
    try {
      const response = await apiClient.get('/api/chat/unread-counts');
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch unread counts',
        counts: {}
      };
    }
  }

  // ============================================================
  // NEW: Queue System (knock/join for sensitive conversations)
  // ============================================================

  /**
   * Submit a knock request to join a sensitive conversation
   * 
   * @param {string} conversationId - ID of the conversation
   * @param {string} message - Optional message to include with the knock
   * @returns {Promise}
   */
  async knockConversation(conversationId, message = '') {
    try {
      const response = await apiClient.post('/api/chat/queue/knock', { conversationId, message });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to submit knock request',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Approve a pending knock request
   * 
   * @param {string} requestId - ID of the queue request to approve
   * @returns {Promise}
   */
  async approveKnock(requestId) {
    try {
      const response = await apiClient.post('/api/chat/queue/approve', { requestId });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to approve knock',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Deny a pending knock request
   * 
   * @param {string} requestId - ID of the queue request to deny
   * @param {string} note - Optional note explaining the denial
   * @returns {Promise}
   */
  async denyKnock(requestId, note = '') {
    try {
      const response = await apiClient.post('/api/chat/queue/deny', { requestId, note });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to deny knock',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Get the pending knock queue for a conversation
   * 
   * @param {string} conversationId - ID of the conversation
   * @returns {Promise}
   */
  async getQueue(conversationId) {
    try {
      const response = await apiClient.get(`/api/chat/queue/${conversationId}`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch queue',
        queue: []
      };
    }
  }

  /**
   * Cancel your own pending knock request
   * 
   * @param {string} requestId - ID of the request to cancel
   * @returns {Promise}
   */
  async cancelKnock(requestId) {
    try {
      const response = await apiClient.post('/api/chat/queue/cancel', { requestId });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to cancel knock',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // REPLY-POST & STANCE SYSTEM
  // ============================================================

  /**
   * Get user post states (suppressed/hidden/muted) for all conversations
   * @returns {Promise} - { success, states: { conversationId: { state, isMuted } } }
   */
  async getUserPostStates() {
    try {
      const response = await apiClient.get('/api/chat/post-states');
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch post states',
        states: {}
      };
    }
  }

  /**
   * Set user post state for a specific conversation
   * @param {string} conversationId - ID of the conversation (REQ-06: changed from messageId)
   * @param {string} state - Post state ('default'|'suppressed'|'hidden') (REQ-06: enum updated)
   * @param {boolean} isMuted - Whether notifications/sounds are muted (REQ-06)
   * @returns {Promise}
   */
  async setUserPostState(conversationId, state, isMuted) {
    try {
      const response = await apiClient.post('/api/chat/post-state', { conversationId, state, isMuted });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to set post state',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Leave a conversation (REQ-19: snooze or abandon)
   * @param {string} conversationId - ID of the conversation to leave
   * @param {string} leaveType - 'snooze' (can rejoin) or 'abandon' (permanent)
   * @returns {Promise}
   */
  async leaveConversation(conversationId, leaveType = 'snooze') {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/leave`, { leaveType });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to leave conversation',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Toggle mute state for a conversation
   * @param {string} conversationId - ID of the conversation
   * @param {boolean} muted - Whether to mute (true) or unmute (false)
   * @returns {Promise}
   */
  async toggleConversationMute(conversationId, muted) {
    try {
      const response = await apiClient.put(`/api/chat/conversations/${conversationId}/mute`, { muted });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to toggle mute',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Get paginated replies for a specific message (reply-post expansion)
   * @param {string} messageId - ID of the parent message
   * @param {Object} options - { limit, offset, all }
   * @returns {Promise} - { success, replies, tallies, totalCount }
   */
  async getMessageReplies(messageId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);
      if (options.all) params.append('all', 'true');
      const queryString = params.toString();
      const url = `/api/chat/messages/${messageId}/replies${queryString ? '?' + queryString : ''}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch replies',
        replies: [],
        tallies: { agree: 0, disagree: 0 },
        totalCount: 0
      };
    }
  }
  // ============================================================
  // ROLE MANAGEMENT (REQ-17)
  // ============================================================

  /**
   * Promote a participant to moderator
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID to promote
   * @returns {Promise}
   */
  async promoteParticipant(conversationId, userId) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/promote`, { userId, newRole: 'moderator' });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to promote participant',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Transfer admin role to another participant
   * @param {string} conversationId - Conversation ID
   * @param {string} targetUserId - User ID to receive admin role
   * @returns {Promise}
   */
  async transferAdmin(conversationId, targetUserId) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/transfer-admin`, { targetUserId });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to transfer admin',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Kick a participant from a conversation (REQ-17, REQ-31, REQ-32)
   * @param {string} conversationId - Conversation ID
   * @param {string} targetUserId - User ID to kick
   * @param {string} reason - Optional reason for kick
   * @returns {Promise}
   */
  async kickParticipant(conversationId, targetUserId, reason = '') {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/kick`, { targetUserId, reason });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to kick participant',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // ADMIN CHALLENGE SYSTEM (REQ-18)
  // ============================================================

  /**
   * Create an admin challenge
   * @param {string} conversationId - Conversation ID
   * @param {string} reason - Reason for challenging the admin
   * @returns {Promise}
   */
  async createChallenge(conversationId, reason) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/challenge`, { reason });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create challenge',
        error: error.response?.data || error.message
      };
    }
  }

  /**
   * Dismiss an admin challenge (admin-only)
   * @param {string} conversationId - Conversation ID
   * @param {string} challengeId - Challenge ID to dismiss
   * @returns {Promise}
   */
  async dismissChallenge(conversationId, challengeId) {
    try {
      const response = await apiClient.post(`/api/chat/conversations/${conversationId}/challenge/${challengeId}/dismiss`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to dismiss challenge',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // PRIVACY MANAGEMENT (REQ-22, REQ-23)
  // ============================================================

  /**
   * Update conversation privacy level
   * @param {string} conversationId - Conversation ID
   * @param {string} privacyLevel - New privacy level (public|base|community|contributing|kin)
   * @returns {Promise}
   */
  async updatePrivacy(conversationId, privacyLevel) {
    try {
      const response = await apiClient.put(`/api/chat/conversations/${conversationId}/privacy`, { privacyLevel });
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update privacy',
        error: error.response?.data || error.message
      };
    }
  }

  // ============================================================
  // EDIT HISTORY (REQ-25)
  // ============================================================

  /**
   * Fetch edit history for a specific message
   * @param {string} conversationId - Conversation ID (for route path)
   * @param {string} messageId - Message ID to get edit history for
   * @returns {Promise}
   */
  async fetchEditHistory(conversationId, messageId) {
    try {
      const response = await apiClient.get(`/api/chat/conversations/${conversationId}/edit-history/${messageId}`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch edit history',
        editHistory: []
      };
    }
  }
}

export default new ChatService();
